// src/Components/Plans/BuyerPlansModal.js
import React, { useEffect, useState, useContext } from "react";
import client from "../../api/client";
import { AuthContext } from "../../contextStore/AuthContext";
import "./PlansModal.css";

// Helper to load Razorpay script
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function BuyerPlansModal({
  open,
  onClose,
  initialPlans = null,
  listingId = null,
  onPurchased = () => { },
}) {
  const { token, user } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [error, setError] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [showPaymentChoiceFor, setShowPaymentChoiceFor] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // --- Normalize plans ---
  function centsToDecimalString(cents) {
    if (!cents) return "";
    return (Number(cents) / 100).toFixed(2);
  }

  function normalizePlanList(raw) {
    return (raw || []).map((p) => {
      const plan = { ...p };
      plan.mrp_display = centsToDecimalString(p.mrp_cents);
      plan.discounted_display = centsToDecimalString(p.discounted_price_cents);
      plan.price_display = centsToDecimalString(p.price_cents);
      plan.currency = p.currency || "INR";
      plan.description_items = (p.description || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      plan._show_mrp = p.mrp_cents && p.discounted_price_cents && p.mrp_cents > p.discounted_price_cents;
      plan._percent_off = plan._show_mrp
        ? Math.round(((p.mrp_cents - p.discounted_price_cents) / p.mrp_cents) * 100)
        : null;
      return plan;
    });
  }

  // --- Fetch buyer plans ---
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await client.get(`/api/plans?type=buyer`);
        if (res?.data?.plans?.length) setPlans(normalizePlanList(res.data.plans));
        else setFetchError("No buyer plans available.");
      } catch (err) {
        console.error("Error loading buyer plans", err);
        setFetchError("Failed to load buyer plans.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  async function getCurrentUserId() {
    if (user?.id) return user.id;
    try {
      const res = await client.get("/api/auth/me", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return res?.data?.user?.id || null;
    } catch {
      return null;
    }
  }

  // ---------- Mock Payment Flow ----------
  async function runMockFlow(createOrderResponse, plan) {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resPayment = createOrderResponse?.data?.payment;
    if (!resPayment) throw new Error("create-order missing payment response");

    const expires_days = plan.validity_days || 7;

    const mockBody = {
      payment_id: resPayment.id,
      provider_payment_id: `mock_${Date.now()}`,
      type: "plan",
      user_id: resPayment.user_id || (await getCurrentUserId()),
      plan_id: plan.id,
      amount: Number(plan.discounted_display || plan.price_display || 0),
      currency: plan.currency || "INR",
      expires_days: expires_days, // 🔥 ADD THIS
      meta: {
        planType: "buyer",
        planId: plan.id,
        validity_days: expires_days // 🔥 ADD THIS
      },
    };

    console.log("🟢 Mock payment body:", mockBody);

    const mockRes = await client.post("/api/payments/mock/complete", mockBody, { headers });
    if (!mockRes?.data?.ok) throw new Error("Mock payment failed");

    return { activated: true, paymentId: resPayment.id };
  }

  // ---------- Razorpay Flow ----------
  async function runRazorpayFlow(createOrderResponse, plan) {
    const data = createOrderResponse?.data;
    if (!data || !data.ok || !data.razorpay_order)
      throw new Error("Server did not return Razorpay order");

    const razorOrder = data.razorpay_order;
    const keyId = data.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const paymentRow = data.payment;
    const userId = paymentRow.user_id || (await getCurrentUserId());
    const expires_days = plan.validity_days || 7;

    await loadScript("https://checkout.razorpay.com/v1/checkout.js");

    const options = {
      key: keyId,
      amount: razorOrder.amount,
      currency: razorOrder.currency || "INR",
      name: "Old Machine Market",
      description: plan.name || "Buyer Plan",
      order_id: razorOrder.id,
      prefill: { name: user?.name || "", email: user?.email || "" },
      notes: {
        internal_payment_id: paymentRow.id,
        plan_id: plan.id,
        planType: "buyer",
        expires_days: expires_days
      },
      handler: async function (response) {
        try {
          console.log("🟢 Razorpay payment successful, verifying...");

          const verifyRes = await client.post(
            "/api/payments/razorpay/verify",
            {
              razorpay_order_id: razorOrder.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              type: "plan",
              user_id: userId,
              plan_id: plan.id,
              listing_id: listingId || null,
              amount: Number(plan.discounted_display || plan.price_display || 0),
              currency: plan.currency || "INR",
              expires_days: expires_days, // 🔥 ADD THIS
              meta: {
                planType: "buyer",
                planId: plan.id,
                listing_id: listingId || null,
                validity_days: expires_days // 🔥 ADD THIS
              },
            },
            { headers }
          );

          console.log("🟢 Verification response:", verifyRes.data);

          if (verifyRes?.data?.ok || verifyRes?.data?.verified) {
            onPurchased({
              type: "plan",
              planId: plan.id,
              paymentId: paymentRow.id,
            });
            setBuyingPlanId(null);
            onClose?.();
          } else {
            throw new Error("Payment verification failed");
          }
        } catch (err) {
          console.error("Razorpay verify error:", err);
          setError("Payment verification failed or cancelled.");
          setBuyingPlanId(null);
        }
      },
      modal: {
        ondismiss: () => {
          console.warn("Payment cancelled by user");
          setBuyingPlanId(null);
          onPurchased({
            type: "plan",
            planId: plan.id,
            paymentFailed: true,
          });
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  }

  // ---------- Create Order ----------
  async function createOrderOnServer({ plan, provider = "razorpay" }) {
    const userId = await getCurrentUserId();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const amountStr = plan.discounted_display || plan.mrp_display || plan.price_display || "0.00";
    const expires_days = plan.validity_days || 7;

    const body = {
      user_id: userId,
      type: "plan",
      plan_id: plan.id,
      listing_id: listingId || null, // 🔥 ADD listing_id
      amount: Number(amountStr),
      currency: plan.currency || "INR",
      provider,
      meta: {
        planType: "buyer",
        planId: plan.id,
        listing_id: listingId || null, // 🔥 ADD listing_id
        validity_days: expires_days // 🔥 ADD validity_days
      },
    };

    console.log("🟢 Creating order with metadata:", body.meta);

    const res = await client.post("/api/payments/create-order", body, { headers });
    if (!res?.data?.ok) throw new Error("Create order failed");
    return res;
  }

  // ---------- Payment Handling ----------
  function onBuyClick(plan) {
    setError(null);
    setShowPaymentChoiceFor(plan);
  }

  async function handlePaymentChoice(choice, plan) {
    setError(null);
    setBuyingPlanId(plan.id);
    setShowPaymentChoiceFor(null);
    setCheckoutLoading(true);

    try {
      const provider = choice === "razorpay" ? "razorpay" : "mock";
      const createRes = await createOrderOnServer({ plan, provider });

      if (choice === "mock") {
        const mockResult = await runMockFlow(createRes, plan);
        onPurchased({
          type: "plan",
          planId: plan.id,
          paymentId: mockResult.paymentId,
          activated: mockResult.activated,
        });
        onClose?.();
      } else {
        await runRazorpayFlow(createRes, plan);
      }
    } catch (err) {
      console.error("handlePaymentChoice error", err);
      setError(err?.message || "Payment failed");
      setBuyingPlanId(null);
    } finally {
      setCheckoutLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="plans-modal-backdrop">
      <div className="plans-modal">
        <div className="plans-modal-header">
          <h3>Buyer Plans</h3>
          <button className="plans-close-btn" onClick={() => onClose?.()}>
            Close
          </button>
        </div>

        {loading && <div className="plans-loading">Loading plans...</div>}
        {fetchError && <div className="plans-error">{fetchError}</div>}
        {error && <div className="plans-error">{error}</div>}

        <div className="plans-grid">
          {plans && plans.length ? (
            plans.map((p) => {
              const showMrp = p._show_mrp;
              const currencySymbol =
                (p.currency || "INR").toUpperCase() === "INR" ? "₹" : p.currency || "";

              return (
                <div key={p.id} className="plans-card">
                  <div className="plans-card-header">
                    <div>
                      <h4 className="plans-card-title">{p.name}</h4>
                      <ul className="plans-desc-list">
                        {p.description_items.slice(0, 5).map((d, i) => (
                          <li key={i}>
                            <span className="desc-check">✔</span>
                            <span className="desc-text">{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="plans-price-block-vertical">
                      {showMrp && (
                        <div className="mrp-line">
                          <span className="mrp-label">MRP:</span>
                          <span className="mrp-value">
                            {currencySymbol}
                            {p.mrp_display}
                          </span>
                        </div>
                      )}

                      <div className="final-price-large discounted-price">
                        <span className="currency-lg">{currencySymbol}</span>
                        <span className="amount-lg">
                          {p.discounted_display || p.price_display || "0.00"}
                        </span>
                      </div>

                      {p._percent_off ? (
                        <div className="percent-off-badge">{p._percent_off}% OFF</div>
                      ) : null}
                      <div className="tax-note">All prices inclusive of taxes</div>
                    </div>
                  </div>

                  <div className="plans-card-footer">
                    <button
                      className="plans-buy-btn"
                      disabled={buyingPlanId === p.id || checkoutLoading}
                      onClick={() => onBuyClick(p)}
                    >
                      {buyingPlanId === p.id ? "Processing…" : "Buy now"}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            !loading && <div className="plans-no">No buyer plans available.</div>
          )}
        </div>

        {/* ---------- Payment Choice Modal ---------- */}
        {showPaymentChoiceFor && (
          <div className="payment-choice-backdrop">
            <div className="payment-choice-modal">
              <h4>Choose payment method</h4>
              <p>
                Purchasing <strong>{showPaymentChoiceFor.name}</strong>
              </p>
              <div className="payment-choice-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPaymentChoiceFor(null)}
                  disabled={checkoutLoading}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => handlePaymentChoice("razorpay", showPaymentChoiceFor)}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? "Processing..." : "Pay with Razorpay"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}